import { defineStore } from 'pinia';
import { onScopeDispose, ref } from 'vue';

/**
 * Tick 1Hz partage pour tous les compteurs UI (build / research / fleet /
 * meteo). Centralise les setInterval qui s'empilaient dans chaque composant
 * (BuildingCard, ResearchPanel, WeatherPanel, etc.) et garantit que
 * tous les timers de la page tickent simultanement.
 *
 * Usage :
 *   const tick = useNowTick();
 *   tick.start();        // demarre le tick (idempotent)
 *   tick.now              // ref<number> = Date.now(), mise a jour 1x/s
 *   formatRemaining(...)  // helper "1h 29m 47s"
 *
 * Le store demarre/arrete automatiquement via un compteur de refs : quand
 * un composant appelle start(), le compteur augmente ; au unmount on
 * appelle stop() et si plus personne n'ecoute le setInterval s'eteint.
 * Eviter le drift quand la page est inactive (browser throttling) : pas
 * critique ici, on tolere quelques secondes de derive.
 */
export const useNowTick = defineStore('nowTick', () => {
  const now = ref<number>(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;
  let refCount = 0;

  function start(): void {
    refCount += 1;
    if (timer == null) {
      now.value = Date.now();
      timer = setInterval(() => { now.value = Date.now(); }, 1000);
    }
  }

  function stop(): void {
    refCount = Math.max(0, refCount - 1);
    if (refCount === 0 && timer != null) {
      clearInterval(timer);
      timer = null;
    }
  }

  /**
   * Helper Vue Composition : demarre au mount, arrete au unmount. A appeler
   * dans le <script setup> d'un composant qui depend du tick.
   */
  function useTick(): { now: typeof now } {
    start();
    onScopeDispose(stop);
    return { now };
  }

  return { now, start, stop, useTick };
});

/**
 * Format "1h 29m 47s" / "29m 47s" / "47s" / "imminent". Inclut TOUJOURS
 * les secondes (cf user feedback session 5 : "il faudrais aussi mettre les
 * seconde").
 */
export function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'imminent';
  const j = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (j > 0) return `${j}j ${h}h ${m}m ${s}s`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Calcule le restant en secondes a partir d'une date ISO. */
export function remainingSecondsFrom(finishAt: string, now: number): number {
  return Math.max(0, Math.floor((new Date(finishAt).getTime() - now) / 1000));
}
